/**
 * Connection health and sync status.
 *
 * The screen a customer opens when they think something is wrong, so it leads
 * with the three things that are usually the cause: the store is not paired,
 * nothing has been synced, or the embeddings have not caught up.
 */

import { Link } from "@tanstack/react-router";

import {
  useCancelJob,
  useContentStats,
  useJobs,
  useSite,
  useStartSync,
} from "../../api/queries";
import type { Job } from "../../api/types";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Loading,
  Stat,
} from "../../ui/primitives";
import { useSiteContext } from "./SiteLayout";

/**
 * How often to re-read while work is in flight.
 *
 * Two seconds is fast enough to feel live and slow enough that a sync running
 * for ten minutes does not add thousands of requests to a free instance that
 * is also doing the syncing.
 */
const LIVE_MS = 2000;

function isActive(job: Job): boolean {
  return job.status === "pending" || job.status === "running";
}

export function OverviewPage() {
  const { orgId, siteId } = useSiteContext();
  const site = useSite(orgId, siteId);
  const jobs = useJobs(orgId, siteId);

  const running = (jobs.data ?? []).some(isActive);
  // Poll only while something is actually happening.
  const liveJobs = useJobs(orgId, siteId, running ? LIVE_MS : undefined);
  const stats = useContentStats(orgId, siteId, running ? LIVE_MS : undefined);

  const startSync = useStartSync(orgId, siteId);
  const cancel = useCancelJob(orgId, siteId);

  if (jobs.isLoading || stats.isLoading) return <Loading />;

  const rows = liveJobs.data ?? jobs.data ?? [];
  const active = rows.find(isActive);
  const connected = site.data?.status === "connected";
  const disabled = site.data?.status === "disabled";

  return (
    <div className="space-y-6">
      {/* "Connected" only ever means the WordPress plugin paired. A site on
          the script tag never gets there, so once it has synced the nudge
          would be permanent and wrong. */}
      {!connected && !stats.data?.last_synced_at && (
        <Card title="This store is not connected yet">
          <p className="text-sm text-muted">
            The assistant cannot answer anything until its content has been
            synced. On WordPress, pair the plugin. On any other site, add the
            script tag and press Sync now below — we read your pages through
            your sitemap.xml.
          </p>
          <Link to="/sites/$siteId/connect" params={{ siteId }} className="mt-3 inline-block">
            <Button variant="primary">Finish installing</Button>
          </Link>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Products"
          value={stats.data?.products ?? 0}
          hint={
            stats.data && stats.data.product_limit >= 0
              ? `of ${stats.data.product_limit.toLocaleString()} on your plan`
              : undefined
          }
          tone={
            stats.data &&
            stats.data.product_limit >= 0 &&
            stats.data.products >= stats.data.product_limit
              ? "warn"
              : "default"
          }
        />
        <Stat label="Pages" value={stats.data?.pages ?? 0} />
        <Stat
          label="Searchable chunks"
          value={stats.data?.chunks ?? 0}
          hint={
            stats.data?.unembedded_chunks
              ? `${stats.data.unembedded_chunks} still being indexed`
              : undefined
          }
          tone={stats.data?.unembedded_chunks ? "warn" : "default"}
        />
        <Stat
          label="Hidden"
          value={stats.data?.excluded ?? 0}
          hint={stats.data?.excluded ? "Excluded from answers" : undefined}
        />
      </div>

      {Boolean(stats.data?.unembedded_chunks) && (
        <Card title="Indexing in progress">
          <p className="text-sm text-muted">
            {stats.data?.unembedded_chunks} chunks are waiting to be indexed for
            semantic search. Until they are, the assistant can still find them by
            keyword — it just will not match a question that uses different
            words from the page.
          </p>
        </Card>
      )}

      <Card
        title="Content sync"
        action={
          active ? (
            <Button
              size="sm"
              variant="danger"
              onClick={() => cancel.mutate(active.id)}
              disabled={cancel.isPending}
            >
              Cancel
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              onClick={() => startSync.mutate("full_sync")}
              // Not gated on pairing: an unpaired site is synced by crawling its
              // sitemap, and gating it left every script-tag install with an
              // index nothing could fill.
              disabled={startSync.isPending || disabled || stats.data?.syncs_left === 0}
            >
              {startSync.isPending ? "Starting…" : "Sync now"}
            </Button>
          )
        }
      >
        {startSync.error != null && <ErrorNote error={startSync.error} />}

        {stats.data?.syncs_left != null && (
          <p className="mb-3 text-sm text-muted">
            {stats.data.syncs_left === 0
              ? "You have used all the syncs in your plan this period. Upgrade on the Billing page to sync again sooner."
              : `${stats.data.syncs_left} of ${stats.data.sync_limit} syncs left this period.`}
          </p>
        )}

        {rows.length === 0 ? (
          <Empty title="Nothing synced yet">
            Run a sync to pull products and pages into the assistant&rsquo;s index.
          </Empty>
        ) : (
          <ul className="divide-y divide-line">
            {rows.slice(0, 8).map((job) => (
              <li key={job.id}>
                <JobRow job={job} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <p className="text-xs text-muted">
        Last synced:{" "}
        {stats.data?.last_synced_at
          ? new Date(stats.data.last_synced_at).toLocaleString()
          : "never"}
      </p>
    </div>
  );
}

function JobRow({ job }: { job: Job }) {
  const progress =
    job.documents_seen > 0
      ? Math.round(
          ((job.documents_written + job.documents_skipped) / job.documents_seen) * 100
        )
      : 0;

  return (
    <div className="flex flex-wrap items-center gap-3 py-2.5">
      <JobStatusBadge status={job.status} />
      <span className="text-sm font-medium">{job.type.replace("_", " ")}</span>

      {isActive(job) && (
        <div className="flex min-w-40 flex-1 items-center gap-2">
          {/* A real progress element, so the percentage is announced rather
              than only drawn. */}
          <progress
            value={progress}
            max={100}
            aria-label="Sync progress"
            className="h-1.5 w-full"
          />
          <span className="text-xs tabular-nums text-muted">{progress}%</span>
        </div>
      )}

      <span className="ml-auto text-xs tabular-nums text-muted">
        {job.documents_written} written · {job.documents_skipped} unchanged
        {job.documents_failed > 0 && ` · ${job.documents_failed} failed`}
      </span>

      {job.error && (
        <p className="w-full text-xs text-bad" role="alert">
          {job.error}
        </p>
      )}
    </div>
  );
}

export function JobStatusBadge({ status }: { status: string }) {
  if (status === "succeeded") return <Badge tone="good">Done</Badge>;
  if (status === "failed") return <Badge tone="bad">Failed</Badge>;
  if (status === "running") return <Badge tone="brand">Running</Badge>;
  if (status === "cancelled") return <Badge>Cancelled</Badge>;
  return <Badge tone="warn">Queued</Badge>;
}
